import { useGetAllBrandsQuery } from '@codezo/graphql';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface BrandSelectorProps {
	value: string;
	/** `label` is the picked brand's name — keep it if the selector may unmount (e.g. dialog steps). */
	onChange: (v: string, label?: string) => void;
	placeholder?: string;
	skip?: boolean;
	clearable?: boolean;
	disabled?: boolean;
	/** Label shown for a pre-selected value before the user opens the list (e.g. on edit). */
	selectedLabel?: string;
	/**
	 * When set, renders a "Create new brand" action pinned below the list.
	 * Called with the current search text so the create form can be prefilled.
	 */
	onCreateNew?: (query: string) => void;
	/** Extra classes merged into the trigger (e.g. width on a toolbar). */
	className?: string;
}

export function BrandSelector({
	value,
	onChange,
	placeholder = 'Select brand',
	skip = false,
	clearable = false,
	disabled = false,
	selectedLabel: selectedLabelProp,
	onCreateNew,
	className,
}: BrandSelectorProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	/** Remembers what was picked here, so a stale label is never shown for another value. */
	const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);

	const { data, loading } = useGetAllBrandsQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: skip || !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllBrand?.data,
		pageInfo: data?.getAllBrand?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const displayLabel =
		(picked?.id === value ? picked.label : undefined) || selectedLabelProp || undefined;

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange('');
		setPicked(null);
	};

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				if (disabled) return;
				setOpen(next);
				if (!next) {
					setQuery('');
					scroll.reset();
					setPage(1);
				}
			}}
		>
			<PopoverTrigger
				disabled={disabled}
				className={cn(
					'flex h-8 w-full cursor-pointer items-center justify-between rounded-lg border border-line-2 bg-surface-input px-2.5 text-sm outline-none transition-colors hover:bg-surface-input/50',
					!value && 'text-muted-foreground',
					disabled && 'cursor-not-allowed opacity-50',
					className
				)}
			>
				<span className="truncate">{displayLabel || placeholder}</span>
				<div className="flex items-center gap-0.5">
					{clearable && value && (
						<span
							onClick={handleClear}
							className="flex h-4 w-4 items-center justify-center rounded-full text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
						>
							<X size={12} />
						</span>
					)}
					<ChevronDown size={14} className="shrink-0 text-muted-foreground" />
				</div>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<div className="border-b border-line-1 p-2">
					<div className="relative">
						<Search
							size={12}
							className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search…"
							className="h-7 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-8 pr-2 text-xs text-text-1 outline-none placeholder:text-text-4"
							autoFocus
						/>
					</div>
				</div>
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{scroll.initialLoading && (
						<div className="py-4 text-center text-xs text-text-4">Loading…</div>
					)}
					{!loading && scroll.items.length === 0 && (
						<div className="px-2 py-3 text-center text-sm text-text-4">
							{query ? 'No results' : 'No brands'}
						</div>
					)}
					{scroll.items.map((brand) => (
						<button
							key={brand.id}
							onClick={() => {
								if (clearable && value === brand.id) {
									onChange('');
									setPicked(null);
								} else {
									onChange(brand.id, brand.name);
									setPicked({ id: brand.id, label: brand.name });
								}
								setOpen(false);
								setQuery('');
							}}
							className={cn(
								'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
								value === brand.id
									? 'bg-surface-fill text-text-1'
									: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
							)}
						>
							<Check
								size={12}
								className={cn(
									'shrink-0',
									value === brand.id ? 'text-accent' : 'invisible'
								)}
							/>
							{brand.name}
						</button>
					))}
					{scroll.loadingMore && (
						<div className="py-1 text-center text-2xs text-text-4">Loading more…</div>
					)}
					<div ref={scroll.sentinelRef} className="h-1" />
				</div>
				{onCreateNew && (
					<div className="border-t border-line-1 p-1">
						<button
							type="button"
							onClick={() => {
								const seed = query.trim();
								setOpen(false);
								setQuery('');
								onCreateNew(seed);
							}}
							className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<Plus size={12} className="shrink-0 text-text-4" />
							Create new brand
						</button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
